import React, { Component } from "react";
import Genus from './genus-item.jsx';
import ReactPaginate from 'react-paginate';
import { getClassificationFilterOptions, getFilteredClassificationsByClassification } from '../../apis/filters';
import toast from '../../components/toast';
import { getUrlParams, generatePaginationHref, toClassName } from '../../utils/';
import LoaderSection from './loader-section.jsx';
import Filters from './filters.jsx';
import debounce from 'lodash/debounce';
import FilteringBase from './filtering-base';

class FamilyGenusContainer extends FilteringBase {
    constructor(props) {
        super(props);

        this.state = Object.assign({}, this.baseState, {
            filters: [],
            isLoadingFilters: false,
            queryParams: props.VirtualPassthrough ? `?${props.virtualPassthrough}` : '',
            firstSearch: true
        });

        if (props.virtualPassthrough) {
            const passthrough = props.virtualPassthrough.split('=');
            this.filterKey = passthrough[0];
            this.filterValue = passthrough[1];
        }

        this.handleChange = this.handleChange.bind(this);
        this.handlePageClick = this.handlePageClick.bind(this);
        this.loadDebounce = debounce(this.load.bind(this), 1000);
    }

    componentDidMount() {
               
        // get filters
        this.loadFilters()
            .then(data => {
                this.updateFilters();
                // reload filters if any are set
                if (Object.keys(getUrlParams()).length) this.loadFilters();
                let params = getUrlParams();
                let pageNum;
                if (Object.keys(params).length) {
                    pageNum = params.page !== '1' ? params.page : 1;
                    this.setState({ page: pageNum }, () => {
                        this.load();
                    });
                } else {
                    this.load();
                }          
                window.addEventListener('popstate', e => {
                    this.updateFilters();
                    console.log(location.pathname + location.search);
                    let params = getUrlParams();
                    let pageNum;
                    if (Object.keys(params).length) {
                        pageNum = params.page !== '1' ? params.page : 1;
                        this.setState({ page: pageNum }, () => {                           
                            this.load(false);
                        });
                    } else {
                        this.setState({ page: 1 }, () => { this.load(false) });                      
                    }                   
                });
            })
            .catch(error => {
                console.log(error);
                this.setState({
                    isLoading: false
                });
                toast.error('There was an error getting product family filters');
            });
    }

    load(pushState = true) {
        if (this.ajaxPromise) this.ajaxPromise.controller.abort();

        this.setState({
            isLoading: true,
            items: []
        });

        if (pushState && !this.state.firstSearch) {
            history.pushState(null, document.title, this.getUrl());
        }
        this.updatePassthrough();

        // grab filter data from query string
        const params = {
                page: this.state.page,
                pageSize: this.state.pageSize,
                selected: this.getSelectedApiParams()
            };
        // remove empty or undefined params
        Object.keys(params).forEach(key => {
            if (!params[key]) delete params[key];
        });
        // add in virtual category passthrough if available
        if (this.filterKey && this.filterValue) {
            params['filterkey'] = this.filterKey;
            params['filtervalue'] = this.filterValue;
        }

        this.setState({
            firstSearch: false
        });

        this.ajaxPromise = getFilteredClassificationsByClassification(this.props.id, params);
        return this.ajaxPromise
            .then(data => {
                this.setState({
                    items: data.records,
                    totalRecords: data.totalRecords
                });
            })
            .catch(error => {
                console.log(error);
                if (error.name === 'AbortError') return;
                toast.error('There was an error loading products');
            })
            .finally(() => {
                this.setState({
                    isLoading: false
                });
            });
    }

    loadFilters() {
        if (this.filterPromise) this.filterPromise.controller.abort();

        const params = {
            selected: this.getSelectedApiParams()
        };
        if (!params.selected) delete params['selected'];
        this.setState({
            isLoadingFilters: true
        });

        this.filterPromise = getClassificationFilterOptions(this.props.id, params);
        return this.filterPromise
            .then(data => {
                this.resetFilterData(data);
                // map currently selected filters to new filters
                const selected = [];
                for (const [key, value] of Object.entries(this.state.filters)) {
                    value.forEach(v => {
                        if (v.selected) selected.push(v.lookupGuid);
                    });
                }
                for (const [key, value] of Object.entries(data)) {
                    value.forEach(v => {
                        if (selected.indexOf(v.lookupGuid) >= 0) v.selected = true;
                    });
                }
                this.setState({
                    filters: data
                });
                return data;
            })
            .catch(error => {
                console.log(error);
                if (error.name === 'AbortError') return;
            })
            .finally(() => {
                this.setState({
                    isLoadingFilters: false
                });
            });
    }

    getSelectedApiParams() {
        const selected = [];
        for (const [key, value] of Object.entries(this.state.filters)) {
            value.forEach(v => {
                if (v.selected) selected.push(v.lookupGuid);
            });
        }

        return selected.join(',');
    }

    // set the selected state of the filters to false
    resetFilterData(data) {
        for (const [key, value] of Object.entries(data)) {
            value.forEach(v => v.selected = false);
        }
    }

    // update filters on page using the url parameters
    updateFilters() {
        const params = getUrlParams(),
            filters = Object.assign({}, this.state.filters);

        // reset filters to be unselected
        this.resetFilterData(filters);
        // go through params and update selected filters
        for (const paramKey in params) {
            const values = params[paramKey].split('|').join(',').split(',');
            const filterSection = Object.entries(filters).find(([key]) => toClassName(key) === toClassName(paramKey));
            // console.log(filterSection, values);

            if (filterSection) {
                values.forEach(value => {
                    const filter = filterSection[1].find(f => toClassName(f.value.toLowerCase()) === value);
                    if (filter) filter.selected = true;
                });
            }
        }

        // update search query
        if (params.q) this.setState({ q: params.q });

        this.setState({
            filters: filters,
            page: params['page'] ? +params['page'] : 1
        });
    }

    // get all the selected filters at this time
    getAllFilters() {
        const params = {
            q: this.state.q,
            page: this.state.page
        };

        for (const [key, value] of Object.entries(this.state.filters)) {
            value.forEach(v => {
                if (v.selected) {
                    const paramKey = toClassName(key),
                        encodedValue = v.value;
                    if (params[paramKey]) params[paramKey].push(encodedValue);
                    else params[paramKey] = [encodedValue];
                }
            });
        }

        // pipe delimit array values
        Object.keys(params).forEach(key => {
            if (Array.isArray(params[key])) params[key] = params[key].join('|');
        });

        return params;
    }

    // get the url for the current window location
    getUrl() {
        const baseUrl = `${window.location.pathname}`,
            filters = this.getAllFilters(),
            params = [];
        for (const key in filters) {
            let val = filters[key];
            if (((key === 'page' && val > 1) || key !== 'page')) {
                if (typeof val === 'string') val = val.toLowerCase();
                if (val) params.push(`${key}=${encodeURIComponent(val)}`);
            }
        }

        return `${baseUrl}${params.length ? '?' : ''}${params.join('&')}`;
    }

    updatePassthrough() {
        const params = getUrlParams(),
            queryParams = [];

        // make sure passthrough is always set
        if (this.filterKey && !params[this.filterKey]) {
            params[this.filterKey] = this.filterValue;
        }
        // go through params and update selected filters
        for (const paramKey in params) {
            if (paramKey !== 'q' && paramKey !== 'page') queryParams.push(`${toClassName(paramKey)}=${params[paramKey]}`);
        }

        // console.log(queryParams, this.filterKey);
        this.setState({
            queryParams: queryParams.length ? `?${queryParams.join('&')}` : ''
        });
    }

    handleChange(e) {
        const target = event.target,
            isChecked = target.checked,
            value = target.value,
            filters = Object.assign({}, this.state.filters);

        Object.keys(filters).forEach(key => {
            const match = filters[key].find(f => f.lookupGuid === value);
            if (match) match.selected = isChecked;
        });

        this.setState({
            filters: filters,
            page: 1
        }, () => {
            this.loadFilters();
            this.load();
        });
    }
    
    render() {
        let results,
            pagination,
            loader;

        if (this.state.isLoading) loader = <LoaderSection show={true} />;

        if (this.showPagination() && !this.state.isLoading) {
            pagination = (<ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                breakClassName={'break-me'}
                pageCount={this.pages()}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={this.handlePageClick}
                containerClassName={'pagination'}
                subContainerClassName={'pages pagination'}
                activeClassName={'active'}
                forcePage={this.state.page - 1}
                hrefBuilder={generatePaginationHref}
            />);
        }

        if (this.state.items.length > 0) {
            results = (<div className="row">
                {this.state.items.map((genus, index) => (
                    <Genus key={index} {...genus} queryParams={this.state.queryParams} colClass="col-lg-4 col-6 item-col" />
                ))}
            </div>);
        }
        else if (!this.state.isLoading) {
            results = <div><p className="no-results">No results found</p></div>;
        }

        return (
            <div className="row justify-content-center">
                {/* <div className="col-lg-3">
                    <Filters showSearch={false} filterMsg="">
                        {Object.entries(this.state.filters).map(([key, value], index) => (
                            <details className="filter-accordion" open key={key} role="group" aria-labelledby={'filter-' + toClassName(key)}>
                                <summary className="filter-accordion-header" id={'filter-' + toClassName(key)}><span>{key}</span></summary>
                                <div className="filter-accordion-content">
                                    {value.map((filter, index) => (
                                        <div key={index}>
                                            <input className="custom-checkbox square" id={filter.LookupGuid} type="checkbox" name={key} value={filter.LookupGuid} onChange={this.handleChange} checked={filter.Selected} disabled={this.state.isLoadingFilters} />
                                            <label htmlFor={filter.LookupGuid}>
                                                <span>{filter.Name}  <span className="dull">({filter.NumberOfGenus})</span></span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        ))}
                    </Filters>
                </div>*/}
                  
                <div id="results" className="col-lg-10">
                    {loader}
                    {results}
                    {pagination}
                </div>
            </div>
        );
    }
}

export default FamilyGenusContainer;